<template>
	<BaseModal :title="t('title')" v-model="model" noPadding>
		<div class="px-5 pb-5">
			<div class="text-label-secondary -mt-2 px-3 text-center text-sm">
				{{ t('desc') }}
			</div>
			<div class="mt-4 flex flex-col gap-4">
				<InventoryItem
					v-for="item in items"
					alt
					:src="icon(item.modelUuid)"
					:collection="item.collectionName"
					:name="item.modelName"
					:price="item.price"
					:preloader="item.preloader"
					@click="toggle(item.uuid)"
					v-hover="{ impact: 'soft' }"
				>
					<BaseCheckbox :modelValue="selectedItems.includes(item.uuid)" />
				</InventoryItem>
			</div>
		</div>
		<BottomActionsModal class="pt-4">
			<CountAndTotal :count="selectedItems.length" :total="amountCoins" />
			<BaseButton
				size="lg"
				color="primary"
				class="relative mt-3 w-full"
				@click="sell()"
				:loading="loading"
			>
				<div>{{ selectedItems.length ? t('sell') : t('keep') }}</div>
			</BaseButton>
			<div class="text-label-tertiary mt-3 text-center text-xs">
				{{ t('unselected') }}
			</div>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import InventoryItem from '../InventoryItem.vue'
import { icon } from '@/utils/urls'
import { useI18n } from 'vue-i18n'
import BottomActionsModal from '../BottomActionsModal.vue'
import { useInventory } from '@/stores/inventory'
import CountAndTotal from '../CountAndTotal.vue'
import BaseCheckbox from '../BaseCheckbox.vue'
import { useToast } from '@/stores/toast'

const { t } = useI18n({
	messages: {
		en: {
			title: 'delivery failed',
			desc: 'We couldn`t source the following gifts for delivery. You can sell them and pick up some gifts available in our market or try again later.',
			sell: 'sell selected',
			unselected: 'Unselected gifts will return to inventory',
			keep: 'keep all',
			failedToSell: 'Failed to sell selected gifts',
			failedToSellSome: 'Failed to sell {n} of the selected gifts'
		},
		ru: {
			title: 'ошибки доставки',
			desc: 'Мы не смогли закупить и доставить эти гифты. Ты можешь продать их и выбрать доступные гифты на нашем маркете или попробовать позже.',
			sell: 'продать выбранные',
			unselected: 'Невыбранные гифты вернутся в инвентарь',
			keep: 'оставить все',
			failedToSell: 'Не удалось продать выбранные гифты',
			failedToSellSome: 'Не удалось продать {n} из выбранных гифтов'
		}
	}
})

const props = defineProps<{
	items: {
		uuid: string
		modelUuid: string
		collectionName: string
		modelName: string
		price: number
		preloader?: string | null
	}[]
	all?: boolean
}>()

const emit = defineEmits<{
	(e: 'claimed'): void
}>()

const model = defineModel<boolean>()

const amountCoins = computed(() =>
	props.items
		.filter((item) => selectedItems.value.includes(item.uuid))
		.reduce((acc, item) => acc + item.price, 0)
)

const loading = ref(false)

const inventory = useInventory()
const toast = useToast()

const sell = async () => {
	if (loading.value) {
		return
	}

	loading.value = true

	const soldItems = await inventory.sellItems(selectedItems.value)

	if (!soldItems.length) {
		toast.add({
			type: 'error',
			text: t('failedToSell')
		})
	}
	if (soldItems.length !== selectedItems.value.length) {
		toast.add({
			type: 'error',
			text: t('failedToSellSome', selectedItems.value.length - soldItems.length)
		})
	}

	inventory.resetItems(
		inventory.items
			.filter((item) => !soldItems.includes(item.uuid))
			.map((item) => item.uuid)
	)

	model.value = false
	loading.value = false
}

const selectedItems = ref<string[]>([])

const toggle = (uuid: string) => {
	if (loading.value) {
		return
	}

	const index = selectedItems.value.indexOf(uuid)

	if (index === -1) {
		selectedItems.value.push(uuid)
	} else {
		selectedItems.value.splice(index, 1)
	}
}

watch(model, () => {
	if (!model.value) {
		return
	}

	selectedItems.value = props.items.map((item) => item.uuid)
})
</script>
